import { LitComponent } from "./lit-component";

import type { PropertyDeclarations, PropertyValueMap } from "lit";
import type { ProductFormOption } from "src/utils/ProductUtil";

export class OptionSwatches extends LitComponent {
  static properties: PropertyDeclarations = {
    option: { type: Object },
    colorMap: { type: Object },
    formatValue: { type: Function },
  };

  option?: ProductFormOption;
  colorMap?: Record<string, { color?: string; image?: string }>;
  formatValue?: (value: string) => string;

  get app() {
    if (!window.WishlistKing) {
      throw new Error("Wishlist King SDK not initialized");
    }

    return window.WishlistKing;
  }

  render() {
    if (!this.option) {
      return;
    }

    const name = `options[${this.option.name}]`;
    const fieldsetId = `${this.id}-fieldset`;

    return this.lit.html`
      <div class="wk-option-swatches">
        <label class="wk-label" for=${fieldsetId}>${this.option.name}</label>
        <fieldset id=${fieldsetId} class="wk-fieldset">
          ${this.option.optionValues.map((optionValue) =>
            this.renderSwatch({
              value: optionValue.name,
              name,
              color: optionValue.swatch?.color,
              image: optionValue.swatch?.image?.image?.url,
            }),
          )}
        </fieldset>
      </div>
    `;
  }

  renderSwatch({
    value,
    name,
    ...swatch
  }: {
    value: string;
    name: string;
    color?: string | null;
    image?: string | null;
  }) {
    const handle = this.app.utils.url.handlelize(value);
    const inputId = `${this.app.utils.url.handlelize(this.id)}-${handle}-radio`;
    const unavailable = this.option?.unavailableValues?.includes(value);
    const soldout = this.option?.soldoutValues?.includes(value);
    const selected = value === this.option?.selectedValue;

    // TODO: Map selection to variant id
    // Use matrix (2k variant)
    // Remove variants from ShopifyProduct
    // Use matric to map selection to variant id
    // Load variant data as needed
    // We can also use variantBySelectedOptions to load the variant (without knowing the variant id)
    // encodedVariantAvailability (maps options to availability)
    // encodedVariantExistence (maps options to existence)
    // Cannot get variant IDs for all options without loading all variants
    const variantId = undefined;

    const legacyColorSettings = this.getColorSettings(handle, variantId);
    const color = swatch.color ?? legacyColorSettings.color;
    const image = swatch.image ?? legacyColorSettings.image;

    const styles = [
      color ? `background-color: ${color}` : undefined,
      image ? `background-image: url(${image})` : undefined,
    ]
      .filter((v) => !!v)
      .join(";");

    const classes = [
      "wk-swatch",
      soldout && "wk-soldout",
      unavailable && "wk-unavailable",
      selected && "wk-selected",
      color && "wk-has-color",
      image && "wk-has-image",
    ]
      .filter((v) => !!v)
      .join(" ");

    return this.lit.html`
      <div class=${classes}>
        <label class="wk-label" for=${inputId}>${
          this.formatValue?.(value) ?? value
        }</label>
        <div class="wk-control" style=${styles}>
          <input
            id=${inputId}
            class="wk-radio"
            type="radio"
            ?checked=${selected}
            value=${value}
            name=${name}
          />
        </div>
      </div>
    `;
  }

  getColorSettings(handle: string, variantId?: number) {
    if (variantId && this.colorMap?.[variantId]) {
      return this.colorMap[variantId];
    }
    if (this.colorMap?.[handle]) {
      return this.colorMap[handle];
    }
    return {
      color: undefined,
      image: undefined,
    };
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties);

    if (_changedProperties.has("option")) {
      this.classList.toggle(
        "wk-single-option",
        this.option?.values.length === 1,
      );
    }
  }

  createRenderRoot() {
    return this;
  }
}

if (!customElements.get("wk-option-swatches")) {
  customElements.define("wk-option-swatches", OptionSwatches);
}

declare global {
  interface HTMLElementTagNameMap {
    "wk-option-swatches": OptionSwatches;
  }
}
